import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const VendorManagement = () => {
  const { setPageTitle } = usePageContext();
  const [vendors, setVendors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const { hasPermission } = useAuth();

  useEffect(() => {
    setPageTitle('Vendor Management');
    if (hasPermission('vendor', 'read')) {
      fetchVendors();
    } else {
      setLoading(false);
    }
  }, [setPageTitle, hasPermission]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendor');
      setVendors(response.data);
    } catch (error) {
      toast.error('Error fetching vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingVendor) {
        await axios.put(`/api/vendor/${editingVendor._id}`, formData);
        toast.success('Vendor updated successfully');
      } else {
        await axios.post('/api/vendor', formData);
        toast.success('Vendor created successfully');
      }
      setShowModal(false);
      setEditingVendor(null);
      resetForm();
      setSelectedVendors([]);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving vendor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedVendors.map(id => axios.delete(`/api/vendor/${id}`)));
      toast.success(`${selectedVendors.length} vendors deleted successfully`);
      setSelectedVendors([]);
      setShowDeleteModal(false);
      fetchVendors();
    } catch (error) {
      toast.error('Error deleting vendors');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '' });
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData(vendor);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await axios.delete(`/api/vendor/${id}`);
        toast.success('Vendor deleted successfully');
        fetchVendors();
      } catch (error) {
        toast.error('Error deleting vendor');
      }
    }
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasPermission('vendor', 'read')) {
    return (
      <div className="alert alert-danger text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view vendors.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="text-muted mb-0">Manage vendor information and partnerships</p>
        {hasPermission('vendor', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Vendor
          </button>
        )}
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-search text-muted"></i>
            <input
              type="text"
              className="form-control"
              placeholder="Search vendors by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map(vendor => (
                <tr key={vendor._id}>
                  <td className="fw-bold">{vendor.name}</td>
                  <td>{vendor.email}</td>
                  <td>{vendor.phone}</td>
                  <td>{vendor.address || 'N/A'}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {hasPermission('vendor', 'update') && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleEdit(vendor)}
                          style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {hasPermission('vendor', 'delete') && (
                        <button 
                          className="btn btn-secondary"
                          onClick={() => handleDelete(vendor._id)}
                          style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
        {filteredVendors.length === 0 && (
          <div className="text-center py-5">
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>🚚</div>
            <h5 className="text-muted mt-3">No vendors found</h5>
            <p className="text-muted">Add vendors to manage your business partnerships.</p>
            {hasPermission('vendor', 'create') && (
              <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                <i className="fas fa-plus me-2"></i>Add First Vendor
              </button>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingVendor ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingVendor ? 'Update Vendor' : 'Create Vendor'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;